// @ts-nocheck
import { useState, useEffect } from 'react'
import { useQuery, useMutation } from 'react-query'
import { 
  FileText, 
  Save, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  ArrowLeft,
  Play,
  Download,
  Upload,
  Search
} from 'lucide-react'
import { apiService } from '../services/api'

interface ZoneFile {
  filename: string
  domain: string
  size: number
  modified: string
  type: 'forward' | 'reverse'
}

const ZoneFileEditor = () => {
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [validationResult, setValidationResult] = useState<{valid: boolean; output: string} | null>(null)

  // Fetch list of zone files
  const { data: zonesData, isLoading: zonesLoading, refetch: refetchZones } = useQuery(
    'zoneFiles',
    () => apiService.client.get('/zonefiles?action=list').then(r => r.data),
    { refetchInterval: 30000 }
  )

  const zones: ZoneFile[] = zonesData?.data || []

  // Fetch file content when selected
  const { data: fileData, isLoading: fileLoading } = useQuery(
    ['zoneFile', selectedFile],
    () => apiService.client.get(`/zonefiles?action=get&filename=${selectedFile}`).then(r => r.data),
    { enabled: !!selectedFile }
  )

  useEffect(() => {
    if (fileData?.data?.content) {
      setFileContent(fileData.data.content)
      setSaveStatus('idle')
      setValidationResult(null)
    }
  }, [fileData])

  // Save mutation
  const saveMutation = useMutation(
    async () => {
      const response = await apiService.client.post('/zonefiles?action=save', {
        filename: selectedFile,
        content: fileContent
      })
      return response.data
    },
    {
      onSuccess: () => {
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 3000)
      },
      onError: () => {
        setSaveStatus('error')
      }
    }
  )

  // Validate mutation
  const validateMutation = useMutation(
    async () => {
      const domain = fileData?.data?.domain || ''
      const response = await apiService.client.post('/zonefiles?action=validate', {
        filename: selectedFile,
        content: fileContent,
        domain: domain
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        setValidationResult(data.data)
      }
    }
  )

  // Reload zone mutation
  const reloadMutation = useMutation(
    async () => {
      const domain = fileData?.data?.domain || ''
      const response = await apiService.client.post('/zonefiles?action=reload', {
        domain: domain
      })
      return response.data
    }
  )

  const filteredZones = zones.filter(zone =>
    zone.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
    zone.domain.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (selectedFile) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedFile(null)}
              className="flex items-center space-x-2 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Files</span>
            </button>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{selectedFile}</h2>
              <p className="text-slate-500">{fileData?.data?.domain}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {saveStatus === 'saved' && (
              <span className="flex items-center text-green-600">
                <CheckCircle className="h-5 w-5 mr-1" />
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center text-red-600">
                <AlertCircle className="h-5 w-5 mr-1" />
                Error
              </span>
            )}
            <button
              onClick={() => validateMutation.mutate()}
              disabled={validateMutation.isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              <span>{validateMutation.isLoading ? 'Validating...' : 'Validate'}</span>
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
            >
              <Save className="h-4 w-4" />
              <span>{saveMutation.isLoading ? 'Saving...' : 'Save'}</span>
            </button>
            <button
              onClick={() => reloadMutation.mutate()}
              disabled={reloadMutation.isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Play className="h-4 w-4" />
              <span>{reloadMutation.isLoading ? 'Reloading...' : 'Apply'}</span>
            </button>
          </div>
        </div>

        {/* Validation Result */}
        {validationResult && (
          <div className={`p-4 rounded-lg ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center space-x-2">
              {validationResult.valid ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <span className={`font-medium ${validationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                {validationResult.valid ? 'Zone file is valid' : 'Validation failed'}
              </span>
            </div>
            {validationResult.output && (
              <pre className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{validationResult.output}</pre>
            )}
          </div>
        )}

        {/* Editor with Line Numbers */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-slate-700">Zone File Editor</span>
              <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">{fileContent.split('\n').length} lines</span>
              <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">{fileContent.length} chars</span>
            </div>
            <span className="text-xs text-slate-400">Managed zone file</span>
          </div>
          
          {/* Line Numbers + Editor */}
          <div className="flex h-[600px]">
            {/* Line Numbers */}
            <div className="bg-slate-100 border-r border-slate-200 overflow-hidden select-none">
              <pre className="font-mono text-xs text-slate-400 text-right py-4 px-2 leading-5">
                {fileContent.split('\n').map((_, i) => (
                  <div key={i} className="hover:text-slate-600 hover:bg-slate-200 px-1 rounded">{i + 1}</div>
                ))}
              </pre>
            </div>
            
            {/* Text Editor */}
            <textarea
              value={fileContent}
              onChange={(e) => {
                setFileContent(e.target.value)
                setSaveStatus('idle')
              }}
              className="flex-1 font-mono text-sm text-slate-800 bg-white resize-none focus:outline-none p-4 leading-5"
              spellCheck={false}
              style={{ tabSize: 2 }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Zone File Editor</h2>
          <p className="text-slate-500">Edit managed DNS zone files</p>
        </div>
        <button
          onClick={() => refetchZones()}
          className="flex items-center space-x-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search zone files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
      </div>

      {/* Zone Files Grid */}
      {zonesLoading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredZones.map((zone) => (
            <button
              key={zone.filename}
              onClick={() => setSelectedFile(zone.filename)}
              className="text-left p-4 bg-white rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-sky-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">{zone.filename}</h3>
                    <p className="text-sm text-slate-500">{zone.domain}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  zone.type === 'forward' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {zone.type}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                <span>{formatBytes(zone.size)}</span>
                <span>{zone.modified}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {filteredZones.length === 0 && !zonesLoading && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No zone files found</p>
        </div>
      )}
    </div>
  )
}

export default ZoneFileEditor
